import React from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import { Container, Flex, Box, Text, Link } from 'theme-ui';
import ContentText from '@solid-ui-components/ContentText/ContentTextWithHyperlinksSupport';
import Icon from '@solid-ui-components/ContentIcon';
import ContentContainer from '@solid-ui-components/ContentContainer';
import WithDefaultContent from '@solid-ui-blocks/WithDefaultContent';
import LayoutWrapper from '@solid-ui-layout/LayoutWrapper/LayoutWrapper';
import Reveal from '@solid-ui-components/Reveal/Reveal';

const getBackgroundByVariant = (variant) => {
  switch (variant) {
    case 'green':
      return '#F0FDF9';
    case 'blue':
      return '#E0F2FE';
    default:
      return 'gray100';
  }
};

const getHoveredBackgroundByVariant = (variant) => {
  switch (variant) {
    case 'green':
      return '#CCFBEF';
    case 'blue':
      return '#B9E6FE';
    default:
      return 'gray200';
  }
};

const getBorderColorByVariant = (variant) => {
  switch (variant) {
    case 'green':
      return '#168B8A';
    case 'blue':
      return '#026AA2';
    default:
      return '';
  }
};

const getBorderByVariant = (variant) =>
  variant ? `1px solid ${getBorderColorByVariant(variant)}` : 'none';

const getShadowByVariant = (variant) => {
  switch (variant) {
    case 'green':
      return '0px 4px 6px -2px #168B8A08, 0px 12px 22px -4px #168B8A26';
    case 'blue':
      return '0px 4px 6px -2px #165A8B08, 0px 12px 16px -4px #163E8B26';
    default:
      return 'none';
  }
};

const getStyles = (variant) => ({
  accordion: {
    '.accordion': {
      ' > div': {
        boxShadow: 'none',
      },
    },
    '.accordion__button': {
      cursor: 'pointer',
      position: 'relative',
      outline: 'none',
      backgroundColor: getBackgroundByVariant(variant),
      borderRadius: '8px',
      border: getBorderByVariant(variant),
    },
    '.accordion__button:hover': {
      backgroundColor: getHoveredBackgroundByVariant(variant),
    },
    '.accordion__button::before': {
      content: '" "',
      width: '7px',
      height: '7px',
      marginRight: '12px',
      color: getBorderColorByVariant(variant),
      borderBottom: '2px solid',
      borderLeft: '2px solid',
      position: 'absolute',
      right: 4,
      top: '50%',
      transform: 'translate(0, -50%) rotate(225deg)',
    },
    '.accordion__button[aria-expanded="true"]': {
      borderBottom: 'none',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '.accordion__button[aria-expanded="true"]::before, .accordion__button[aria-selected="true"]::before':
    {
      transform: 'translate(0, -50%) rotate(315deg)',
      transition: 'transform 0.35s ease-in',
    },
    '.accordion__button[aria-expanded="false"]::before, .accordion__button[aria-selected="false"]::before':
    {
      transform: 'translate(0, -50%) rotate(225deg)',
      transition: 'transform 0.15s ease-in',
    },
    '[hidden]': {
      display: 'none',
    },
    '.accordion__panel': {
      animation: 'fadeIn 0.25s ease-in',
      backgroundColor: getBackgroundByVariant(variant),
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
      borderBottom: getBorderByVariant(variant),
      borderLeft: getBorderByVariant(variant),
      borderRight: getBorderByVariant(variant),
    },
    '.accordion__panel:hover': {
      backgroundColor: getHoveredBackgroundByVariant(variant),
    },
    '.accordion__item': {
      borderRadius: '8px',
      backgroundColor: getBackgroundByVariant(variant),
      boxShadow: getShadowByVariant(variant),
    },
    '.accordion__item:hover': {
      '.accordion__panel': {
        backgroundColor: getHoveredBackgroundByVariant(variant),
      },
      '.accordion__button': {
        backgroundColor: getHoveredBackgroundByVariant(variant),
      },
      backgroundColor: getHoveredBackgroundByVariant(variant),
    },
    '@keyframes fadeIn': {
      '0%': {
        opacity: 0.5,
        transform: 'translate(0, -15%)',
      },
      '100%': {
        opacity: 1,
        transform: 'translate(0, 0%)',
      },
    },
  },
  container: {
    textAlign: 'center',
    '& h2': {
      mb: [null, 11, null, null, 16],
    },
  },
  wrapper: {
    p: [null, 0],
    maxWidth: [null, '100%', null, null, '790px'],
  },
  accordionContainer: {
    alignItems: 'center',
  },
  mailtoBlock: {
    mt: '30px',
    borderRadius: '8px',
    backgroundColor: '#f0f9ff',
    padding: '15px',
    textAlign: 'center',
  },
  mailtoHeading: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#101828',
    mb: ['20px', '10px']
  },
  mailtoText: {
    fontSize: '16px',
    fontWeight: 400,
    color: '#475467',
    display: 'inline'
  },
  link: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#142585',
    textDecoration: 'none',
    display: 'inline'
  },
});

const FaqBlock01 = ({ content: { text, collection }, variant }) => {
  const styles = getStyles(variant);

  return (
    <LayoutWrapper>
      <Container sx={styles.wrapper}>
        {text && (
          <>
            <Box sx={styles.container}>
              <ContentText content={text} />
            </Box>
          </>
        )}
        <Box sx={styles.accordion}>
          <Accordion allowZeroExpanded>
            {collection?.map(({ container, text, icon }, index) => (
              <ContentContainer
                content={container}
                key={`item-${index}`}
                variant='cards.primary'
                sx={{ '& + &': { mt: 3 } }}
              >
                <Reveal effect={'fadeInUp'}>
                  <AccordionItem uuid={index === 0 ? 1 : undefined} >
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        <Flex sx={styles.accordionContainer} px='14px' py='12px'>
                          <Icon content={icon} size='xs' mr='3' p='2' round />
                          <ContentText content={text?.[0]} mb='0' pr='9' />
                        </Flex>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <ContentText content={text?.[1]} mx='14px' pb='12px' />
                    </AccordionItemPanel>
                  </AccordionItem>
                </Reveal>
              </ContentContainer>
            ))}
          </Accordion>
        </Box>
        <Reveal effect='fadeInUp'>
          <Box sx={styles.mailtoBlock}>
            <Text sx={styles.mailtoHeading}> Have a question left unanswered? </Text>
            <Text sx={styles.mailtoText}> Feel free to send an email to </Text>
            <Link sx={styles.link} href="mailto:support@targpatrol.com">
              support@targpatrol.com
            </Link>
          </Box>
        </Reveal>
      </Container>
    </LayoutWrapper>
  );
};

export default WithDefaultContent(FaqBlock01);
